import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LBtn, LModal, LModalFooterButtons } from "@components";
import { convertUTCToLocalTimeZone } from "@helpers/dateHelpers";
import { format } from "date-fns";
import { parseHistoryStatus } from "@helpers/enumHelper";
import { getDetails } from "@helpers/examineeHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { useApi } from "@services/useApi";
import { ExistingExamineeDetails } from "@store/generalTypes";
import { UpcomingAssessment, updateUpcomingAssessments } from "@store/locationsSlice";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useAssessmentHistory } from "../../useAssessmentHistory";
import "../../assessmentHistory.css";
import { ExamineeContactInfo, updateContactInfo } from "../../store/newExamineeSlice";
import { ExamineeCard } from "../ExamineeCard";
import { EditExaminee } from "./EditExaminee";
import { StartAssessmentModal } from "./start-assessment/StartAssessmentModal";

export const RightSide = ({ upcomingAssessment, completed }: RightSideProps): JSX.Element => {
    const [selectedExamineeDetails, setSelectedExamineeDetails] = useState<ExistingExamineeDetails>();
    const [beginAssessmentOpen, setBeginAssessmentOpen] = useState(false);
    const [editExamineeOpen, setEditExamineeOpen] = useState(false);

    const { getExamineeDetails, loading, updateExaminee, getUpcomingAssessments } = useApi();

    const dispatch = useAppDispatch();

    const { location, organizationAssessments } = useAppSelector((x) => {
        return {
            location: x.orgLocations.currentLocation,
            organizationAssessments: x.organizationAssessments
        };
    });

    const { isFacilitator } = useUserPermission({ useCurrentLocation: true });

    const { history: assessmentHistory, error: historyError } = useAssessmentHistory(
        location?.id,
        upcomingAssessment?.examineeId
    );

    const handleGetExamineeDetails = async (): Promise<void> => {
        const examinee = await getExamineeDetails(location!.id, Number(upcomingAssessment!.examineeId));
        setSelectedExamineeDetails(examinee);
    };

    const handleGetUpcomingAssessments = async (): Promise<void> => {
        if (completed) {
            return;
        }

        const assessments = await getUpcomingAssessments(location!.id);
        dispatch(updateUpcomingAssessments(assessments));
    };

    const toggleBeginAssessment = (): void => {
        const contactInfo: ExamineeContactInfo = {
            firstName: selectedExamineeDetails!.firstName,
            lastName: selectedExamineeDetails!.lastName,
            email: selectedExamineeDetails!.email ?? "",
            id: 0,
            phoneNumber: ""
        };
        dispatch(updateContactInfo(contactInfo));

        setBeginAssessmentOpen(!beginAssessmentOpen);
    };

    const handleOnStartAssessmentClose = (): void => {
        toggleBeginAssessment();
    };

    const toggleEditExaminee = (): void => {
        setEditExamineeOpen(!editExamineeOpen);
    };

    const confirmEditExaminee = async (): Promise<void> => {
        await handleGetExamineeDetails();
        await handleGetUpcomingAssessments();
        setEditExamineeOpen(false);
    };

    const handleEditExamineeEmail = (): void => {
        setEditExamineeOpen(true);
    };

    useEffect(() => {
        setSelectedExamineeDetails(undefined);

        if (upcomingAssessment?.id) {
            void handleGetExamineeDetails();
        }
    }, [upcomingAssessment?.id]);

    if (!upcomingAssessment) {
        return <div className="column has-background-white is-bordered-top is-bordered-bottom" />;
    }

    const examineeContactInfo: ExamineeContactInfo = {
        id: selectedExamineeDetails?.id ?? upcomingAssessment.examineeId,
        firstName: selectedExamineeDetails?.firstName ?? upcomingAssessment.examineeFirstName,
        lastName: selectedExamineeDetails?.lastName ?? upcomingAssessment.examineeLastName,
        email: selectedExamineeDetails?.email ?? upcomingAssessment.examineeEmail,
        phoneNumber: selectedExamineeDetails?.phoneNumber ?? upcomingAssessment.examineePhoneNumber
    };

    return (
        <>
            <div className="column has-background-white is-bordered-top is-bordered-bottom">
                <ExamineeCard
                    contactInfo={examineeContactInfo}
                    bgColor="#fff"
                    loading={loading}
                    classList="p-5"
                    examineeDetails={selectedExamineeDetails && getDetails(selectedExamineeDetails)}
                    bigLabels
                    clinician={
                        selectedExamineeDetails &&
                        `${selectedExamineeDetails?.clinicianFirstName} ${selectedExamineeDetails?.clinicianLastName}`
                    }
                    onEdit={toggleEditExaminee}
                />

                <div className="is-flex is-flex-direction-column mx-5">
                    {!completed && isFacilitator && (
                        <>
                            <div className="divider" />
                            <div style={{ maxWidth: "50%" }}>
                                <LBtn
                                    smallSpacing
                                    classes="px-5"
                                    onClick={toggleBeginAssessment}
                                    isBold
                                    disabled={!organizationAssessments.hasAssessment}
                                    toolTipText={
                                        "Your account is out of assessments. More can be purchased in the \"Manage\" section of the LASSI-D™ portal."
                                    }
                                >
                                    Select Assessment Form
                                </LBtn>
                            </div>
                        </>
                    )}
                    {completed && isFacilitator && (
                        <>
                            <div className="divider" />
                            <div style={{ maxWidth: "50%" }}>
                                <LBtn
                                    smallSpacing
                                    classes="px-5"
                                    onClick={toggleBeginAssessment}
                                    disabled={!organizationAssessments.hasAssessment}
                                    toolTipText={
                                        "Your account is out of assessments. More can be purchased in the \"Manage\" section of the LASSI-D™ portal."
                                    }
                                >
                                    New Assessment
                                </LBtn>
                            </div>
                            <p className="has-text-weight-semibold has-text-grey my-4">
                                Allow for at least 6 months between assessments
                            </p>
                        </>
                    )}
                </div>

                {historyError && (
                    <p role="alert">Unable to load assessment history. Please reopen this view to retry.</p>
                )}
                {!isEmpty(assessmentHistory) && (
                    <>
                        <div className="divider" />
                        <div className="is-flex is-flex-direction-column mx-5 mb-4">
                            <p className="has-text-weight-semibold has-text-grey mb-4">HISTORY</p>
                            {assessmentHistory?.map((hs, idx) => {
                                return (
                                    <div
                                        className="columns is-flex is-flex-direction-row is-align-items-center"
                                        key={`history-${hs.displayId}-${idx}`}
                                    >
                                        <div className="column is-3">
                                            <p className="has-text-weight-semibold has-text-ldarkgrey">
                                                {format(convertUTCToLocalTimeZone(hs.launchedDate), "P")}
                                            </p>
                                        </div>
                                        <div className="column assessment-history-details">
                                            {hs.assessmentFormName}
                                            <div className="mx-2">-</div>
                                            <p className="has-text-weight-semibold assessment-history-status">
                                                {parseHistoryStatus(hs).replace("Pre-", "Pre\u2011")}
                                            </p>
                                            <div className="mx-2">-</div>
                                            <p className="has-text-weight-semibold">
                                                {hs.assessmentType === "InPerson" ? "In-person" : hs.assessmentType}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* START ASSESSMENT */}
            {beginAssessmentOpen && (
                <StartAssessmentModal
                    examinee={examineeContactInfo}
                    locationId={Number(location?.id)}
                    upcomingAssessmentId={completed ? undefined : upcomingAssessment.id}
                    onClose={handleOnStartAssessmentClose}
                    onEditExamineeEmail={handleEditExamineeEmail}
                />
            )}

            {/* EDIT EXAMINEE */}
            {editExamineeOpen && (
                <LModal
                    onClose={toggleEditExaminee}
                    onConfirm={confirmEditExaminee}
                    footer={<LModalFooterButtons confirmText="Save" cancelText="Cancel" centered loading={loading} />}
                    title={<p className="has-text-centered has-text-weight-bold is-size-3">Edit Examinee Info</p>}
                    width={"m"}
                >
                    <EditExaminee
                        contactInfo={examineeContactInfo}
                        details={selectedExamineeDetails}
                        handleUpdate={updateExaminee}
                    />
                </LModal>
            )}
        </>
    );
};

interface RightSideProps {
    upcomingAssessment?: UpcomingAssessment;
    completed?: boolean;
}
